<script lang="ts">
  // Theme toggle — flips the persisted light/dark theme
  // (wiki/feature-design-system.md §Theming, AC5). Icons are inline SVG (NOT a
  // Nerd Font webfont — wiki §Nerd Font note / AC3). The toggle reads + writes
  // the shared `theme` store, which mirrors the choice onto `data-theme` and
  // localStorage.
  import { theme, type Theme } from '$lib/theme/themeStore';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';

  let current = $state<Theme>('light');
  const unsub = theme.subscribe((value) => (current = value));
  $effect(() => () => unsub());

  const isDark = $derived(current === 'dark');
</script>

<button
  type="button"
  class="vd-theme-toggle"
  aria-label={isDark ? t(UiKeys.Theme.SwitchToLight) : t(UiKeys.Theme.SwitchToDark)}
  aria-pressed={isDark}
  title={isDark ? t(UiKeys.Theme.LightTheme) : t(UiKeys.Theme.DarkTheme)}
  onclick={() => theme.toggle()}
>
  {#if isDark}
    <!-- moon -->
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none"
      stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  {:else}
    <!-- sun -->
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none"
      stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  {/if}
</button>

<style lang="scss">
  .vd-theme-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 10px;
    border: 1px solid var(--line);
    background: transparent;
    color: var(--ink-soft);
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      color 0.2s ease;

    &:hover {
      border-color: var(--accent);
      color: var(--accent-text);
    }

    &:focus-visible {
      outline: 2px solid var(--accent-text);
      outline-offset: 2px;
    }
  }
</style>
